import { z } from "zod";
import AreaChartView from "@/components/ui/area-chart/AreaChartView";
import type { ChartConfig } from "@/components/ui/ChartTooltip";
import { chartHexColorSchema } from "@/lib/utils/chartUtils";

type TAggregateMode = "sum" | "count" | "avg";

/** Coerces tool/LLM row values to a finite number or null. */
export const toNumber = (value: unknown): number | null => {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/** Shared by {@link ChatLineChartSpec} — aggregate rows by xKey for area/line charts. */
export const buildAreaData = (input: {
  data: Array<Record<string, unknown>>;
  xKey: string;
  yKey: string;
  aggregate: TAggregateMode | null;
}): Array<Record<string, string | number>> => {
  const { data, xKey, yKey, aggregate } = input;

  if (aggregate == null) {
    return data
      .map((row) => {
        const x = row[xKey];
        const y = toNumber(row[yKey]);
        if (x == null || y == null) return null;
        return { [xKey]: String(x), [yKey]: y };
      })
      .filter((row): row is Record<string, string | number> => row != null);
  }

  const grouped = new Map<string, { sum: number; count: number }>();

  for (const row of data) {
    const xRaw = row[xKey];
    if (xRaw == null) continue;
    const x = String(xRaw);
    const current = grouped.get(x) ?? { sum: 0, count: 0 };

    if (aggregate === "count") {
      current.count += 1;
      grouped.set(x, current);
      continue;
    }

    const y = toNumber(row[yKey]);
    if (y == null) continue;
    current.sum += y;
    current.count += 1;
    grouped.set(x, current);
  }

  const normalized: Array<Record<string, string | number>> = [];
  for (const [x, stats] of grouped.entries()) {
    const value =
      aggregate === "count"
        ? stats.count
        : aggregate === "avg"
          ? stats.count === 0
            ? 0
            : stats.sum / stats.count
          : stats.sum;
    normalized.push({
      [xKey]: x,
      [yKey]: Number(value.toFixed(2)),
    });
  }

  return normalized;
};

export const ChatAreaChartSpec = {
  props: z.object({
    title: z.string().nullable().optional(),
    data: z.array(z.record(z.string(), z.unknown())),
    xKey: z.string(),
    yKey: z.string(),
    aggregate: z.enum(["sum", "count", "avg"]).nullable().optional(),
    gradient: z.boolean().nullable().optional(),
    height: z.number().nullable().optional(),
    color: chartHexColorSchema.nullable().optional(),
  }),
  description:
    'Area chart with filled region beneath the line. Great for volume and trend visualization. Props: title (string or null), data (array of records), xKey (string), yKey (string), aggregate ("sum" | "count" | "avg" or null), gradient (boolean or null), height (number or null), color (optional #RGB or #RRGGBB series colour). Use { "$state": "/path" } to bind read-only data.',
  example: {
    title: "Sentiment volume trend",
    data: [
      { day: "2026-03-01", volume: 124 },
      { day: "2026-03-02", volume: 188 },
      { day: "2026-03-03", volume: 143 },
      { day: "2026-03-04", volume: 201 },
    ],
    xKey: "day",
    yKey: "volume",
    aggregate: null,
    gradient: true,
    height: 280,
  },
} as const;

/** Parses props and renders {@link AreaChartView}. */
export const ChatAreaChartFromProps = ({
  props,
}: {
  props: Record<string, unknown>;
}) => {
  const parsed = ChatAreaChartSpec.props.safeParse(props);
  if (!parsed.success) return null;

  const typed = parsed.data;
  const chartData = buildAreaData({
    data: typed.data,
    xKey: typed.xKey,
    yKey: typed.yKey,
    aggregate: typed.aggregate ?? null,
  });

  const chartConfig = {
    [typed.yKey]: {
      label:
        typed.yKey.length > 0
          ? `${typed.yKey.charAt(0).toUpperCase()}${typed.yKey.slice(1)}`
          : typed.yKey,
      color: typed.color ?? "var(--chart-1)",
    },
  } satisfies ChartConfig;

  return (
    <AreaChartView
      title={typed.title ?? null}
      data={chartData}
      chartConfig={chartConfig}
      xAxisKey={typed.xKey}
      dataKey={typed.yKey}
      gradient={typed.gradient ?? true}
      height={typed.height ?? null}
    />
  );
};
